import Loader from '@/components/Loader';
import ScrolledCard from '@/components/ScrolledCard';
import Constants from '@/Constants';
import { ReportProcess } from '@/models/ReportProcess';
import { getHost } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { listReportProcessesRequest } from '@/store/report/action';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import './style.scss';

interface Props {
    show: boolean;
    close: () => void;
    reportId: number;
    reportName?: string;
}

const ReportHistoryModal: React.FC<Props> = ({ show, close, reportId, reportName }: Props) => {
    const { strings, reportProcess, loading } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        reportProcess: state.report.data,
        loading: state.report.loading,
    }));
    const [reportProcessList, setReportProcessList] = useState<ReportProcess[]>([]);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(listReportProcessesRequest(reportId));
    }, []);

    useEffect(() => {
        if (reportProcess) {
            const result = reportProcess.filter((it) => it.reportId === reportId)[0];
            if (result) {
                setReportProcessList(result.reportProcesses);
            }
        }
    }, [reportProcess]);

    const formatMessageWithDateAtTime = (message: string, dateTime: Date) =>
        message.replace('{0}', moment(dateTime).format('DD/MM/YYYY')).replace('{1}', moment(dateTime).format('HH:mm'));

    const formatMessageWithStartAndEndDate = (message: string, reportProcess: ReportProcess) => {
        let startDate, finishDate;

        if (reportProcess.customFilters) {
            const parsedFilters = JSON.parse(reportProcess.customFilters);
            if (parsedFilters.PeriodoDados) {
                startDate = parsedFilters.PeriodoDados[0];
                finishDate = parsedFilters.PeriodoDados[1];
            }
        } else {
            finishDate = reportProcess.finishDate;
            if (reportProcess.startDate) {
                startDate = reportProcess.startDate;
            } else {
                startDate = reportProcess.creationDate;
            }
        }

        return startDate && finishDate
            ? message.replace('{0}', moment(startDate).format('DD/MM/YYYY')).replace('{1}', moment(finishDate).format('DD/MM/YYYY'))
            : null;
    };

    const getReportProcessItem = (reportProcess: ReportProcess) => {
        switch (reportProcess.status) {
            case Constants.reportProcessStatus.COMPLETED:
                const reportUri = `${getHost()}${Constants.API_PATH}${Constants.TRACKING_REPORTS}/${reportId}/download-async?token=${
                    reportProcess.reportToken
                }`;

                return (
                    <div className="report-history-item" key={_.uniqueId('reportProcess_')}>
                        <div className="report-history-item__info">
                            <h3>{formatMessageWithDateAtTime(strings.DATA_PAGE.GENERATED_ON, reportProcess.startDate)}</h3>
                            <span>{formatMessageWithStartAndEndDate(strings.DATA_PAGE.REPORT_PERIOD_MSG, reportProcess)}</span>
                        </div>
                        <a href={reportUri} rel="noreferrer" target="_blank">
                            <i className="material-icons">{Constants.icons.SAVE_ALT}</i>
                        </a>
                    </div>
                );

            case Constants.reportProcessStatus.COMPLETED_WITH_ERRORS:
                return (
                    <div className="report-history-item" key={_.uniqueId('reportProcess_')}>
                        <div className="report-history-item__info">
                            <h3>{strings.DATA_PAGE.ERROR}</h3>
                            {reportProcess.finishDate && (
                                <span>{formatMessageWithDateAtTime(strings.DATA_PAGE.GENERATED_ERROR, reportProcess.finishDate)}</span>
                            )}
                        </div>
                        <i className="material-icons-outlined">{Constants.icons.ERROR_OUTLINE}</i>
                    </div>
                );
        }
    };

    return (
        <Modal backdrop="static" centered contentClassName="report-history-modal" onHide={close} show={show}>
            <Modal.Header className="report-history-modal__header">
                <i className="material-icons-outlined" onClick={close}>
                    {Constants.icons.CLOSE}
                </i>
                <div className="report-history-modal__header__content">
                    <h3>{strings.DATA_PAGE.HISTORY}</h3>
                    <span className="subtitle">{reportName}</span>
                    <span className="txt-warning">{strings.DATA_PAGE.HISTORY_ALERT}</span>
                </div>
            </Modal.Header>
            <Modal.Body>
                <ScrolledCard list={reportProcessList}>
                    {loading ? <Loader /> : reportProcessList.map((reportProcess) => getReportProcessItem(reportProcess))}
                </ScrolledCard>
            </Modal.Body>
        </Modal>
    );
};

export default ReportHistoryModal;
